import { doc, firestore, onSnapshot } from '@ps/functions/firebase/firestore';

import type { ExtendedCommunityChannelMessage } from '../../types/ExtendedCommunityChannelMessage';
import { firestoreDocToMessage } from './firestoreDocToMessage';

/**
 * Live listener on a SINGLE message document, by id — the web port of
 * PopshopRN's `communityChannelMessageDoc$`. Because it targets the doc by id
 * (no `createdAt` filter), it fires on ANY change to that message regardless of
 * age: reaction-count bumps, edits, reply-count changes, pin/unpin, delete. This
 * is the per-message realtime tier the list-level subscription (anchored on the
 * newest seed) can't cover for older/seeded messages.
 *
 * Emits `null` when the doc doesn't exist (hard-deleted).
 */
export function subscribeToChannelMessageDoc(
  creatorId: string,
  channelId: string,
  messageId: string,
  onChange: (message: ExtendedCommunityChannelMessage | null) => void,
  onError?: (error: Error) => void,
): () => void {
  const db = firestore();
  if (!db) return () => {};

  const ref = doc(
    db,
    'stores',
    creatorId,
    'communityChannels',
    channelId,
    'messages',
    messageId,
  );

  return onSnapshot(
    ref,
    snapshot => {
      if (!snapshot.exists()) {
        onChange(null);
        return;
      }
      onChange(firestoreDocToMessage(channelId, snapshot) ?? null);
    },
    error => onError?.(error),
  );
}
